import { User } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import ClubBadge from "../../ClubBadge";
import { getClubBadge } from "../../../data/clubBadges";
import ConfirmModal from "../../ConfirmModal";
import { useIsMobile } from "../../../hooks/useIsMobile";
import {
  fetchGameweekOps, fetchGameweekImpact, patchGameweek, submitGameweek, reopenGameweek,
  fetchGameweekFromFpl, patchAdminFixture, fetchCompetitionPlayers,
  GameweekOpsData, GameweekImpact, CompetitionPlayer, GoalsPrefillSource, LeagueTableRow,
} from "../../../api/admin";
import { gameweekSections, GwSectionKey } from "../../../lib/admin/gameweekSections";
import { competitionModeLabel } from "../../../lib/admin/competitionModes";
import { resolveGameweekPrefill } from "../../../lib/admin/gameweekOpsPrefill";
import {
  card, primaryBtn, secondaryBtn, dangerBtn, smallBtn,
  inputStyle, tableStyle, thStyle, tdStyle,
  sectionLabel, badge, errorStyle, panelHeading,
} from "../../../styles";

interface Props {
  user: User;
  competitionId: string;
  reload?: () => Promise<void>;
  /** Overrides the back-link target; defaults to the owner-facing "../../gameweeks" relative
   * path. The standalone /my-gameweeks/:competitionId/:gwId route passes "/my-gameweeks" here,
   * since the relative default would resolve to a nonexistent route on that URL shape. */
  backTo?: string;
}

interface StatRow {
  playerId: string;
  playerName: string;
  goals: number;
  assists: number;
  yellowCards: number;
  redCards: number;
}

interface StandingRow {
  clubId: string;
  position: number;
}

export default function GameweekOpsPage({ user, competitionId, reload, backTo }: Props) {
  const { t } = useTranslation();
  const { gwId } = useParams<{ gwId: string }>();

  const [data, setData] = useState<GameweekOpsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [statRows, setStatRows] = useState<StatRow[]>([]);
  const [standingRows, setStandingRows] = useState<StandingRow[]>([]);
  const [goalsThisRound, setGoalsThisRound] = useState<number | "">("");
  const [gwSearch, setGwSearch] = useState("");
  const [gwSearchResults, setGwSearchResults] = useState<CompetitionPlayer[]>([]);

  const [fixtureEdits, setFixtureEdits] = useState<Record<string, { homeScore: string; awayScore: string; status: string }>>({});
  const [fixtureSavingId, setFixtureSavingId] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const [confirmComplete, setConfirmComplete] = useState(false);
  const [confirmReopen, setConfirmReopen] = useState(false);
  const [impact, setImpact] = useState<GameweekImpact | null>(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [reopening, setReopening] = useState(false);

  const [fetchingProposal, setFetchingProposal] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [shortNameDraft, setShortNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);

  const standingDragIdx = useRef<number | null>(null);
  const standingDragOverIdx = useRef<number | null>(null);
  const [standingDragVisual, setStandingDragVisual] = useState<number | null>(null);

  const load = async () => {
    if (!gwId) return;
    setLoading(true);
    setLoadError("");
    try {
      const d = await fetchGameweekOps(user, competitionId, gwId);
      setData(d);
      const edits: Record<string, { homeScore: string; awayScore: string; status: string }> = {};
      for (const f of d.fixtures) {
        edits[f.id] = { homeScore: f.homeScore !== null ? String(f.homeScore) : "", awayScore: f.awayScore !== null ? String(f.awayScore) : "", status: f.status };
      }
      setFixtureEdits(edits);

      const prefill = resolveGameweekPrefill(d);
      setStatRows(prefill.statRows);
      setStandingRows(prefill.standingRows);
      setGoalsThisRound(prefill.goalsThisRound);
    } catch {
      setLoadError(t("admin.gameweekOps.errors.failedToLoad"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [gwId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only reachable when the form already has submitted stats to overwrite (see the Apply
  // button's own guard) — everything else is prefilled automatically on load. Preserves any
  // card value the incoming row doesn't carry (older proposals predate card ingestion and
  // always omit yellowCards/redCards) rather than zeroing a manually-entered card.
  const applyProposalToForm = () => {
    if (!data?.proposal || data.proposal.status !== "READY") return;
    const existingById = new Map(statRows.map((r) => [r.playerId, r]));
    setStatRows(data.proposal.payload.stats.map((s) => ({
      playerId: s.playerId,
      playerName: s.playerName,
      goals: s.goals,
      assists: s.assists,
      yellowCards: s.yellowCards ?? existingById.get(s.playerId)?.yellowCards ?? 0,
      redCards: s.redCards ?? existingById.get(s.playerId)?.redCards ?? 0,
    })));
    setGoalsThisRound(data.proposal.payload.goalsThisRound);
    setConfirmApply(false);
  };

  const handleFetchNow = async () => {
    if (!gwId) return;
    setFetchingProposal(true);
    setSaveError("");
    try {
      await fetchGameweekFromFpl(user, competitionId, gwId);
      await load();
    } catch {
      setSaveError(t("admin.gameweekOps.errors.fetchFailed"));
    } finally {
      setFetchingProposal(false);
    }
  };

  const searchGwPlayers = async (q: string) => {
    if (!gwId || !q.trim()) { setGwSearchResults([]); return; }
    try {
      const compId = data?.competition.id;
      if (!compId) return;
      const results = await fetchCompetitionPlayers(user, compId, { search: q });
      setGwSearchResults(results.players);
    } catch { /* ignore */ }
  };

  const moveStanding = (fromIdx: number, toIdx: number) => {
    if (fromIdx === toIdx) return;
    setStandingRows((prev) => {
      const sorted = [...prev].sort((a, b) => a.position - b.position);
      const [moved] = sorted.splice(fromIdx, 1);
      sorted.splice(toIdx, 0, moved);
      return sorted.map((r, idx) => ({ ...r, position: idx + 1 }));
    });
  };

  const currentPayload = () => {
    const stats = statRows.map((r) => ({ playerId: r.playerId, goals: r.goals, assists: r.assists, yellowCards: r.yellowCards, redCards: r.redCards }));
    const standings = (data?.competition.clubs.length ?? 0) > 0 ? standingRows.map((r) => ({ clubId: r.clubId, position: r.position })) : undefined;
    const goals = goalsThisRound !== "" ? Number(goalsThisRound) : undefined;
    return { stats, standings, goals };
  };

  const handleSaveSection = async () => {
    if (!gwId) return;
    setSaving(true);
    setSaveError("");
    try {
      const { stats, standings, goals } = currentPayload();
      await submitGameweek(user, competitionId, gwId, stats, standings, goals, false);
      await load();
      await reload?.();
    } catch {
      setSaveError(t("admin.gameweekOps.errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const loadImpact = async () => {
    if (!gwId) return;
    setImpactLoading(true);
    try {
      setImpact(await fetchGameweekImpact(user, competitionId, gwId));
    } catch {
      setImpact(null);
    } finally {
      setImpactLoading(false);
    }
  };

  const openCompleteConfirm = () => { setConfirmComplete(true); loadImpact(); };
  const openReopenConfirm = () => { setConfirmReopen(true); loadImpact(); };

  const handleComplete = async () => {
    if (!gwId) return;
    setSaving(true);
    setSaveError("");
    try {
      const { stats, standings, goals } = currentPayload();
      await submitGameweek(user, competitionId, gwId, stats, standings, goals, true);
      setConfirmComplete(false);
      await load();
      await reload?.();
    } catch {
      setSaveError(t("admin.gameweekOps.errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleReopen = async () => {
    if (!gwId) return;
    setReopening(true);
    setSaveError("");
    try {
      await reopenGameweek(user, competitionId, gwId);
      setConfirmReopen(false);
      await load();
      await reload?.();
    } catch {
      setSaveError(t("admin.competitions.errors.failedToReopen"));
    } finally {
      setReopening(false);
    }
  };

  const startEditingName = () => {
    if (!data) return;
    setNameDraft(data.gameweek.name);
    setShortNameDraft(data.gameweek.shortName ?? "");
    setEditingName(true);
  };

  const handleSaveName = async () => {
    if (!gwId || !nameDraft.trim()) return;
    setSavingName(true);
    try {
      await patchGameweek(user, competitionId, gwId, { name: nameDraft.trim(), shortName: shortNameDraft.trim() || null });
      setEditingName(false);
      await load();
      await reload?.();
    } catch {
      setSaveError(t("admin.gameweekOps.errors.saveFailed"));
    } finally {
      setSavingName(false);
    }
  };

  const handleSaveFixture = async (fixtureId: string) => {
    const edit = fixtureEdits[fixtureId];
    if (!edit) return;
    setFixtureSavingId(fixtureId);
    try {
      const homeScoreVal = edit.homeScore !== "" ? parseInt(edit.homeScore, 10) : null;
      const awayScoreVal = edit.awayScore !== "" ? parseInt(edit.awayScore, 10) : null;
      const updated = await patchAdminFixture(user, competitionId, gwId!, fixtureId, { status: edit.status, homeScore: homeScoreVal, awayScore: awayScoreVal });
      setData((d) => d ? { ...d, fixtures: d.fixtures.map((f) => f.id === fixtureId ? updated : f) } : d);
      setFixtureEdits((prev) => ({ ...prev, [fixtureId]: { homeScore: updated.homeScore !== null ? String(updated.homeScore) : "", awayScore: updated.awayScore !== null ? String(updated.awayScore) : "", status: updated.status } }));
    } catch {
      setSaveError(t("admin.gameweekOps.errors.saveFailed"));
    } finally {
      setFixtureSavingId(null);
    }
  };

  if (loading) return <p style={{ color: "var(--color-text-muted)", padding: "1rem" }}>{t("common.loading")}</p>;
  if (loadError || !data) return <p style={errorStyle}>{loadError || t("admin.gameweekOps.errors.failedToLoad")}</p>;

  const sections = gameweekSections({
    gameweekNumber: data.gameweek.number,
    supportedModes: data.competition.supportedModes,
    sourceType: data.competition.sourceType,
    hasClubs: data.competition.clubs.length > 0,
  });

  const canReopen = data.gameweek.completedAt != null && data.gameweek.number > 0;

  // Mirrors the gating in load() (both call resolveGameweekPrefill) so the provenance notes and
  // the Apply button agree with what was actually prefilled, without duplicating that decision.
  const {
    notCompleted, proposalStats, statsPrefilled,
    standingsSource, standingsTable, goalsSource, statsProvisional, goalsProvisional,
    canApplyProposal, coverageEmpty,
  } = resolveGameweekPrefill(data);
  const droppedByApply = canApplyProposal
    ? statRows.filter((r) => !proposalStats.some((s) => s.playerId === r.playerId)).length
    : 0;

  return (
    <div>
      <div style={{ marginBottom: "0.75rem" }}>
        <Link
          to={backTo ?? "../../gameweeks"}
          {...(backTo ? {} : { relative: "path" as const })}
          style={{ fontSize: "0.82rem", color: "var(--color-text-muted)", textDecoration: "none" }}
        >
          ← {t("admin.gameweekOps.backToSchedule")}
        </Link>
      </div>

      {saveError && <p style={{ ...errorStyle, marginBottom: "1rem" }}>{saveError}</p>}

      {/* ── Header ── */}
      <div style={{ ...card, marginBottom: "1rem" }}>
        {editingName ? (
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
            <input style={{ ...inputStyle, maxWidth: "220px" }} value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} />
            <input style={{ ...inputStyle, maxWidth: "100px" }} maxLength={8} placeholder={t("admin.gameweekOps.shortNamePlaceholder")} value={shortNameDraft} onChange={(e) => setShortNameDraft(e.target.value)} />
            <button style={smallBtn} onClick={handleSaveName} disabled={savingName || !nameDraft.trim()}>{savingName ? t("common.saving") : t("common.save")}</button>
            <button style={smallBtn} onClick={() => setEditingName(false)} disabled={savingName}>{t("common.cancel")}</button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
            <h1 style={{ margin: 0, fontSize: "1.3rem", fontWeight: 700, color: "var(--color-text)" }}>
              {data.gameweek.name}
              {data.gameweek.shortName && <span style={{ fontWeight: 400, color: "var(--color-text-muted)", marginLeft: "0.4rem" }}>({data.gameweek.shortName})</span>}
            </h1>
            <button style={smallBtn} onClick={startEditingName}>{t("common.edit")}</button>
            {data.gameweek.completedAt
              ? <span style={{ ...badge, background: "var(--color-surface-alt)", color: "var(--color-text-muted)", border: "1px solid var(--color-border-subtle)" }}>{t("admin.competitions.gwList.completed")}</span>
              : <span style={{ ...badge, background: "var(--color-status-active-bg)", color: "var(--color-status-active-text)" }}>{t("admin.competitions.gwList.open")}</span>}
          </div>
        )}
      </div>

      {data.gameweek.fplProvisional && data.gameweek.completedAt && (
        <div style={{ ...card, marginBottom: "1rem", borderLeft: "3px solid var(--color-warning)" }}>
          <p style={{ ...sectionLabel, margin: 0 }}>{t("admin.gameweekOps.autoSubmit.provisional.title")}</p>
          <p style={{ margin: "0.3rem 0 0", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
            {t("admin.gameweekOps.autoSubmit.provisional.body")}
          </p>
        </div>
      )}

      {/* ── FPL proposal panel ── */}
      {data.competition.sourceType === "FPL" && (
        <div style={{ ...card, marginBottom: "1rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap" }}>
            <div>
              <p style={{ ...sectionLabel, margin: 0 }}>{t("admin.gameweekOps.proposal.title")}</p>
              {data.proposal?.status === "READY" && (
                <p style={{ margin: "0.3rem 0 0", fontSize: "0.82rem", color: "var(--color-status-active-text)" }}>
                  {t("admin.fplAutomation.proposal.readyBanner", { time: new Date(data.proposal.fetchedAt).toLocaleString() })}
                </p>
              )}
              {data.proposal?.status === "INCOMPLETE" && (
                <p style={{ margin: "0.3rem 0 0", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
                  {t("admin.fplAutomation.proposal.incompleteBanner", { time: new Date(data.proposal.fetchedAt).toLocaleString() })}
                  {data.proposal.errorMessage ? ` ${data.proposal.errorMessage}` : ""}
                </p>
              )}
              {data.proposal?.status === "FAILED" && (
                <p style={{ margin: "0.3rem 0 0", fontSize: "0.82rem", color: "var(--color-danger)" }}>
                  {t("admin.fplAutomation.proposal.failedBanner", { error: data.proposal.errorMessage ?? "" })}
                  {" "}{t("admin.competitions.gwList.fetchedAt", { date: new Date(data.proposal.fetchedAt).toLocaleString() })}
                </p>
              )}
              {!data.proposal && <p style={{ margin: "0.3rem 0 0", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>{t("admin.gameweekOps.proposal.none")}</p>}
              {data.proposal?.status === "READY" && data.proposal.payload.autoSubmit && (() => {
                const autoSubmit = data.proposal.payload.autoSubmit;
                return (
                  <>
                    {!autoSubmit.eligible && (
                      <div style={{ margin: "0.5rem 0 0" }}>
                        <p style={{ margin: 0, fontSize: "0.82rem", fontWeight: 600, color: "var(--color-warning)" }}>
                          {t("admin.gameweekOps.autoSubmit.heldTitle")}
                        </p>
                        <ul style={{ margin: "0.25rem 0 0", paddingLeft: "1.1rem", fontSize: "0.8rem", color: "var(--color-warning)" }}>
                          {autoSubmit.blocks.map((b) => (
                            <li key={b.code}>{t(`admin.gameweekOps.autoSubmit.blocks.${b.code}`, { detail: b.detail, defaultValue: b.message })}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {autoSubmit.eligible && data.proposal.confirmedAt && (
                      <p style={{ margin: "0.4rem 0 0", fontSize: "0.82rem", color: "var(--color-status-active-text)" }}>
                        {t("admin.gameweekOps.autoSubmit.completedAutomatically", { time: new Date(data.proposal.confirmedAt).toLocaleString() })}
                      </p>
                    )}
                    {autoSubmit.warnings.length > 0 && (
                      <ul style={{ margin: "0.4rem 0 0", paddingLeft: "1.1rem", fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                        {autoSubmit.warnings.map((w) => (
                          <li key={w.code}>{t(`admin.gameweekOps.autoSubmit.warnings.${w.code}`, { detail: w.detail, defaultValue: w.message })}</li>
                        ))}
                      </ul>
                    )}
                    {autoSubmit.lastError && (
                      <p style={{ margin: "0.4rem 0 0", fontSize: "0.82rem", color: "var(--color-danger)" }}>
                        {t("admin.gameweekOps.autoSubmit.lastError", { error: autoSubmit.lastError })}
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
            <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
              <button style={secondaryBtn} onClick={handleFetchNow} disabled={fetchingProposal}>
                {fetchingProposal ? t("admin.fplAutomation.proposal.fetching") : t("admin.fplAutomation.proposal.fetchNow")}
              </button>
              {canApplyProposal && (
                <button style={primaryBtn} onClick={() => setConfirmApply(true)}>{t("admin.gameweekOps.proposal.applyToForm")}</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Sections ── */}
      {sections.map((section) => (
        <SectionShell
          key={section.key}
          sectionKey={section.key}
          feeds={section.feeds}
          reasonKey={section.reasonKey}
          onSave={handleSaveSection}
          saving={saving}
        >
          {section.key === "fixtures" && (
            <FixturesSection
              fixtures={data.fixtures}
              fixtureEdits={fixtureEdits}
              setFixtureEdits={setFixtureEdits}
              fixtureSavingId={fixtureSavingId}
              onSaveFixture={handleSaveFixture}
            />
          )}
          {section.key === "playerStats" && (
            <PlayerStatsSection
              statRows={statRows}
              setStatRows={setStatRows}
              gwSearch={gwSearch}
              setGwSearch={setGwSearch}
              gwSearchResults={gwSearchResults}
              setGwSearchResults={setGwSearchResults}
              onSearch={searchGwPlayers}
              prefilled={statsPrefilled}
              provisional={statsProvisional}
              coverageEmpty={coverageEmpty}
            />
          )}
          {section.key === "totalGoals" && (
            <TotalGoalsSection goalsThisRound={goalsThisRound} setGoalsThisRound={setGoalsThisRound} source={goalsSource} mismatch={notCompleted && data.goalsPrefill.mismatch} provisional={goalsProvisional} />
          )}
          {section.key === "standings" && (
            <StandingsSection
              standingRows={standingRows}
              clubs={data.competition.clubs}
              source={standingsSource}
              table={standingsTable}
              standingDragIdx={standingDragIdx}
              standingDragOverIdx={standingDragOverIdx}
              standingDragVisual={standingDragVisual}
              setStandingDragVisual={setStandingDragVisual}
              moveStanding={moveStanding}
            />
          )}
        </SectionShell>
      ))}

      {/* ── Primary actions ── */}
      <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem", flexWrap: "wrap" }}>
        <button style={primaryBtn} onClick={openCompleteConfirm} disabled={saving}>{t("admin.gameweekOps.completeBtn")}</button>
        {canReopen && (
          <button style={dangerBtn} onClick={openReopenConfirm}>{t("admin.competitions.actions.reopenGW")}</button>
        )}
      </div>

      {confirmComplete && (
        <ConfirmModal
          title={t("admin.gameweekOps.completeConfirm.title")}
          message={t("admin.gameweekOps.completeConfirm.message", { name: data.gameweek.name })}
          confirmLabel={t("admin.gameweekOps.completeBtn")}
          danger={false}
          confirming={saving}
          onConfirm={handleComplete}
          onCancel={() => setConfirmComplete(false)}
        >
          {(statsProvisional || goalsProvisional) && (
            <p style={{ margin: "0 0 0.6rem", fontSize: "0.82rem", color: "var(--color-warning)" }}>
              {t("admin.gameweekOps.proposal.provisional")}
            </p>
          )}
          <ImpactSummary impact={impact} loading={impactLoading} />
        </ConfirmModal>
      )}

      {confirmReopen && (
        <ConfirmModal
          title={t("admin.competitions.gwList.reopenConfirm.title")}
          message={t("admin.competitions.gwList.reopenConfirm.message", { name: data.gameweek.name })}
          confirmLabel={t("admin.competitions.actions.reopenGW")}
          confirming={reopening}
          onConfirm={handleReopen}
          onCancel={() => setConfirmReopen(false)}
        >
          <ImpactSummary impact={impact} loading={impactLoading} />
        </ConfirmModal>
      )}

      {confirmApply && data.proposal?.status === "READY" && (
        <ConfirmModal
          title={t("admin.gameweekOps.proposal.applyConfirm.title")}
          message={t("admin.gameweekOps.proposal.applyConfirm.message", { current: statRows.length, incoming: proposalStats.length })}
          confirmLabel={t("admin.gameweekOps.proposal.applyConfirm.confirm")}
          danger={false}
          onConfirm={applyProposalToForm}
          onCancel={() => setConfirmApply(false)}
        >
          {droppedByApply > 0 && (
            <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-warning)" }}>
              {t("admin.gameweekOps.proposal.applyConfirm.dropped", { count: droppedByApply })}
            </p>
          )}
        </ConfirmModal>
      )}
    </div>
  );
}

// ─── Impact summary — shown inside both the Complete and Reopen confirm dialogs ──────

function ImpactSummary({ impact, loading }: { impact: GameweekImpact | null; loading: boolean }) {
  const { t } = useTranslation();
  if (loading) return <p style={{ fontSize: "0.82rem", color: "var(--color-text-muted)", margin: 0 }}>{t("common.loading")}</p>;
  if (!impact) return null;
  return (
    <div style={{ fontSize: "0.82rem", color: "var(--color-text-muted)", background: "var(--color-surface-alt)", borderRadius: "8px", padding: "0.6rem 0.75rem", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
      <span>{t("admin.gameweekOps.impact.rows", { stats: impact.playerStatRows, standings: impact.standingRows })}</span>
      {impact.leagues.length > 0 && <span>{t("admin.gameweekOps.impact.leagues", { count: impact.leagues.length })}</span>}
      {impact.leaguesThatWillComplete > 0 && <span>{t("admin.gameweekOps.impact.willComplete", { count: impact.leaguesThatWillComplete })}</span>}
      {impact.leaguesThatWillReopen > 0 && <span>{t("admin.gameweekOps.impact.willReopen", { count: impact.leaguesThatWillReopen })}</span>}
      {impact.gwChallengeLocksAffected && <span>{t("admin.gameweekOps.impact.gwChallengeLocks")}</span>}
      {impact.fpLocksAffected > 0 && <span>{t("admin.gameweekOps.impact.fpLocks", { count: impact.fpLocksAffected })}</span>}
    </div>
  );
}

// ─── Section shell — heading, feeds chips, save button, children ─────────────────────

function SectionShell({ sectionKey, feeds, reasonKey, onSave, saving, children }: {
  sectionKey: GwSectionKey; feeds: string[]; reasonKey: string; onSave: () => void; saving: boolean; children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ ...card, marginBottom: "1rem" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.75rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
            <h2 style={{ ...panelHeading, margin: 0 }}>{t(`admin.gameweekOps.sections.${sectionKey}.title`)}</h2>
            {feeds.map((mode) => (
              <span key={mode} style={{ ...badge, background: "var(--color-surface-alt)", color: "var(--color-text-dim)", fontSize: "0.68rem" }}>
                {competitionModeLabel(mode)}
              </span>
            ))}
          </div>
          <p style={{ margin: "0.25rem 0 0", fontSize: "0.78rem", color: "var(--color-text-dim)" }}>{t(reasonKey)}</p>
        </div>
        {sectionKey !== "fixtures" && (
          <button style={smallBtn} onClick={onSave} disabled={saving}>{saving ? t("common.saving") : t("admin.gameweekOps.saveSection")}</button>
        )}
      </div>
      {children}
    </div>
  );
}

// ─── Fixtures section ──────────────────────────────────────────────────────────────

function FixturesSection({ fixtures, fixtureEdits, setFixtureEdits, fixtureSavingId, onSaveFixture }: {
  fixtures: GameweekOpsData["fixtures"];
  fixtureEdits: Record<string, { homeScore: string; awayScore: string; status: string }>;
  setFixtureEdits: React.Dispatch<React.SetStateAction<Record<string, { homeScore: string; awayScore: string; status: string }>>>;
  fixtureSavingId: string | null;
  onSaveFixture: (id: string) => void;
}) {
  const { t } = useTranslation();
  if (fixtures.length === 0) return <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "0.85rem" }}>{t("admin.gameweekOps.sections.fixtures.none")}</p>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      {fixtures.map((fixture) => {
        const edit = fixtureEdits[fixture.id] ?? { homeScore: "", awayScore: "", status: fixture.status };
        return (
          <div key={fixture.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", fontSize: "0.85rem" }}>
            <span style={{ flex: 1, textAlign: "right", minWidth: "80px", color: "var(--color-text)" }}>{fixture.homeClub.name}</span>
            <input type="number" min={0} max={99} value={edit.homeScore}
              onChange={(e) => setFixtureEdits((prev) => ({ ...prev, [fixture.id]: { ...edit, homeScore: e.target.value } }))}
              style={{ width: "44px", fontSize: "0.85rem", padding: "3px 6px", borderRadius: "6px", border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-text)", textAlign: "center" }} />
            <span style={{ color: "var(--color-text-muted)" }}>–</span>
            <input type="number" min={0} max={99} value={edit.awayScore}
              onChange={(e) => setFixtureEdits((prev) => ({ ...prev, [fixture.id]: { ...edit, awayScore: e.target.value } }))}
              style={{ width: "44px", fontSize: "0.85rem", padding: "3px 6px", borderRadius: "6px", border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-text)", textAlign: "center" }} />
            <span style={{ flex: 1, minWidth: "80px", color: "var(--color-text)" }}>{fixture.awayClub.name}</span>
            <select value={edit.status}
              onChange={(e) => setFixtureEdits((prev) => ({ ...prev, [fixture.id]: { ...edit, status: e.target.value } }))}
              style={{ fontSize: "0.8rem", padding: "3px 6px", borderRadius: "6px", border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-text)", minWidth: "110px" }}>
              <option value="SCHEDULED">Scheduled</option>
              <option value="LIVE">Live</option>
              <option value="FINISHED">Finished</option>
              <option value="POSTPONED">Postponed</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="ABANDONED">Abandoned</option>
            </select>
            <button style={smallBtn} disabled={fixtureSavingId === fixture.id} onClick={() => onSaveFixture(fixture.id)}>
              {fixtureSavingId === fixture.id ? t("common.saving") : t("common.save")}
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ─── Player stats section ──────────────────────────────────────────────────────────

function PlayerStatsSection({ statRows, setStatRows, gwSearch, setGwSearch, gwSearchResults, setGwSearchResults, onSearch, prefilled, provisional, coverageEmpty }: {
  statRows: StatRow[]; setStatRows: React.Dispatch<React.SetStateAction<StatRow[]>>;
  gwSearch: string; setGwSearch: (v: string) => void;
  gwSearchResults: CompetitionPlayer[]; setGwSearchResults: (v: CompetitionPlayer[]) => void;
  onSearch: (q: string) => void;
  prefilled: boolean;
  provisional: boolean;
  coverageEmpty: { unmatchedPlayer: number; unmatchedClub: number } | null;
}) {
  const { t } = useTranslation();
  return (
    <>
      {prefilled && (
        <p style={{ margin: "0 0 0.75rem", fontSize: "0.8rem", padding: "0.5rem 0.75rem", borderRadius: "8px", background: "var(--color-surface-alt)", color: "var(--color-text-muted)" }}>
          {t("admin.gameweekOps.sections.playerStats.provenance.FPL")}
        </p>
      )}
      {provisional && (
        <p style={{ margin: "0 0 0.75rem", fontSize: "0.8rem", padding: "0.5rem 0.75rem", borderRadius: "8px", background: "var(--color-surface-alt)", color: "var(--color-warning)" }}>
          {t("admin.gameweekOps.proposal.provisional")}
        </p>
      )}
      {coverageEmpty && (
        <div style={{ margin: "0 0 0.75rem", fontSize: "0.8rem", padding: "0.5rem 0.75rem", borderRadius: "8px", background: "var(--color-surface-alt)", color: "var(--color-warning)" }}>
          <p style={{ margin: 0 }}>{t("admin.gameweekOps.proposal.emptyWarning")}</p>
          <p style={{ margin: "0.2rem 0 0" }}>{t("admin.gameweekOps.proposal.coverage", { unmatchedPlayers: coverageEmpty.unmatchedPlayer, unmatchedClubs: coverageEmpty.unmatchedClub })}</p>
        </div>
      )}
      <div style={{ position: "relative", marginBottom: "1rem" }}>
        <input style={inputStyle} placeholder={t("admin.gameweekOps.sections.playerStats.searchPlaceholder")}
          value={gwSearch} onChange={(e) => { setGwSearch(e.target.value); onSearch(e.target.value); }} />
        {gwSearchResults.length > 0 && (
          <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "6px", zIndex: 10, maxHeight: "200px", overflowY: "auto" }}>
            {gwSearchResults.map((p) => (
              <div key={p.playerId} style={{ padding: "0.4rem 0.75rem", cursor: "pointer", fontSize: "0.9rem", color: "var(--color-text)" }}
                onClick={() => {
                  setStatRows((rows) => rows.find((r) => r.playerId === p.playerId) ? rows : [...rows, { playerId: p.playerId, playerName: p.name, goals: 0, assists: 0, yellowCards: 0, redCards: 0 }]);
                  setGwSearch(""); setGwSearchResults([]);
                }}>
                {p.name} — {p.club.name}
              </div>
            ))}
          </div>
        )}
      </div>
      {statRows.length > 0 ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ ...tableStyle, marginBottom: 0 }}>
            <thead>
              <tr>
                <th style={thStyle}>Player</th>
                <th style={{ ...thStyle, width: "70px" }}>Goals</th>
                <th style={{ ...thStyle, width: "70px" }}>Assists</th>
                <th style={{ ...thStyle, width: "70px" }}>Yellow</th>
                <th style={{ ...thStyle, width: "70px" }}>Red</th>
                <th style={{ ...thStyle, width: "40px" }}></th>
              </tr>
            </thead>
            <tbody>
              {statRows.map((row, i) => (
                <tr key={row.playerId}>
                  <td style={tdStyle}>{row.playerName}</td>
                  <td style={tdStyle}>
                    <input type="number" min={0} max={20} value={row.goals}
                      style={{ ...inputStyle, width: "60px", padding: "0.25rem 0.4rem" }}
                      onChange={(e) => setStatRows((rows) => rows.map((r, j) => j === i ? { ...r, goals: Number(e.target.value) } : r))}
                      onFocus={(e) => e.target.select()} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" min={0} max={20} value={row.assists}
                      style={{ ...inputStyle, width: "60px", padding: "0.25rem 0.4rem" }}
                      onChange={(e) => setStatRows((rows) => rows.map((r, j) => j === i ? { ...r, assists: Number(e.target.value) } : r))}
                      onFocus={(e) => e.target.select()} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" min={0} max={2} value={row.yellowCards}
                      style={{ ...inputStyle, width: "60px", padding: "0.25rem 0.4rem" }}
                      onChange={(e) => setStatRows((rows) => rows.map((r, j) => j === i ? { ...r, yellowCards: Number(e.target.value) } : r))}
                      onFocus={(e) => e.target.select()} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" min={0} max={1} value={row.redCards}
                      style={{ ...inputStyle, width: "60px", padding: "0.25rem 0.4rem" }}
                      onChange={(e) => setStatRows((rows) => rows.map((r, j) => j === i ? { ...r, redCards: Number(e.target.value) } : r))}
                      onFocus={(e) => e.target.select()} />
                  </td>
                  <td style={tdStyle}>
                    <button style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-danger)" }}
                      onClick={() => setStatRows((rows) => rows.filter((_, j) => j !== i))}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", margin: 0 }}>{t("admin.gameweekOps.sections.playerStats.none")}</p>
      )}
    </>
  );
}

// ─── Total goals section ───────────────────────────────────────────────────────────

function TotalGoalsSection({ goalsThisRound, setGoalsThisRound, source, mismatch, provisional }: {
  goalsThisRound: number | ""; setGoalsThisRound: (v: number | "") => void;
  source: GoalsPrefillSource | null;
  mismatch: boolean;
  provisional: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div>
      {source && source !== "NONE" && (
        <p style={{ margin: "0 0 0.75rem", fontSize: "0.8rem", padding: "0.5rem 0.75rem", borderRadius: "8px", background: "var(--color-surface-alt)", color: "var(--color-text-muted)" }}>
          {t(`admin.gameweekOps.sections.totalGoals.provenance.${source}`)}
        </p>
      )}
      {provisional && (
        <p style={{ margin: "0 0 0.75rem", fontSize: "0.8rem", padding: "0.5rem 0.75rem", borderRadius: "8px", background: "var(--color-surface-alt)", color: "var(--color-warning)" }}>
          {t("admin.gameweekOps.proposal.provisional")}
        </p>
      )}
      {mismatch && (
        <p style={{ margin: "0 0 0.75rem", fontSize: "0.8rem", padding: "0.5rem 0.75rem", borderRadius: "8px", background: "var(--color-surface-alt)", color: "var(--color-warning)" }}>
          {t("admin.gameweekOps.sections.totalGoals.mismatch")}
        </p>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <label style={{ fontSize: "0.82rem", color: "var(--color-text-muted)", flexShrink: 0 }}>{t("admin.gameweekOps.sections.totalGoals.label")}</label>
        <input type="number" min={0} value={goalsThisRound} placeholder="0"
          style={{ ...inputStyle, width: 80, padding: "0.3rem 0.5rem" }}
          onChange={(e) => setGoalsThisRound(e.target.value === "" ? "" : Math.max(0, Number(e.target.value)))}
          onFocus={(e) => e.target.select()} />
      </div>
    </div>
  );
}

// ─── Standings section ─────────────────────────────────────────────────────────────

// Desktop shows the full derived line; mobile keeps only the two numbers a table is actually
// scanned by (points, goal difference) — eight numeric columns overflow at a 320px width.
type LeagueTableStatKey = keyof Omit<LeagueTableRow, "clubId">;
const DESKTOP_STAT_COLUMNS: { key: LeagueTableStatKey; label: string }[] = [
  { key: "played", label: "P" }, { key: "wins", label: "W" }, { key: "draws", label: "D" }, { key: "losses", label: "L" },
  { key: "goalsFor", label: "GF" }, { key: "goalsAgainst", label: "GA" }, { key: "goalDifference", label: "GD" }, { key: "points", label: "Pts" },
];
const MOBILE_STAT_COLUMNS: { key: LeagueTableStatKey; label: string }[] = [
  { key: "goalDifference", label: "GD" }, { key: "points", label: "Pts" },
];

const statCellStyle: React.CSSProperties = { width: "1.9rem", textAlign: "right", fontSize: "0.75rem", color: "var(--color-text-muted)", flexShrink: 0 };

/** Clubs level on points, GD, and goals-for are a genuine tie under the PL's own rules (see
 * deriveLeagueTable's doc comment) — this groups the derived table by that key so tied rows can
 * be flagged instead of presenting one arbitrary order with no signal that it was arbitrary. */
function tieKey(row: LeagueTableRow): string {
  return `${row.points}|${row.goalDifference}|${row.goalsFor}`;
}

function StandingsSection({ standingRows, clubs, source, table, standingDragIdx, standingDragOverIdx, standingDragVisual, setStandingDragVisual, moveStanding }: {
  standingRows: StandingRow[];
  clubs: GameweekOpsData["competition"]["clubs"];
  source: "DERIVED" | "PREVIOUS" | "NONE" | null;
  table: LeagueTableRow[] | null;
  standingDragIdx: React.MutableRefObject<number | null>;
  standingDragOverIdx: React.MutableRefObject<number | null>;
  standingDragVisual: number | null;
  setStandingDragVisual: (v: number | null) => void;
  moveStanding: (from: number, to: number) => void;
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  if (clubs.length === 0) return <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "0.85rem" }}>{t("admin.gameweekOps.sections.standings.noClubs")}</p>;

  const tableByClub = new Map((table ?? []).map((row) => [row.clubId, row]));
  const tieCounts = new Map<string, number>();
  for (const row of table ?? []) {
    const key = tieKey(row);
    tieCounts.set(key, (tieCounts.get(key) ?? 0) + 1);
  }
  const hasAnyTie = Array.from(tieCounts.values()).some((count) => count > 1);
  const statColumns = isMobile ? MOBILE_STAT_COLUMNS : DESKTOP_STAT_COLUMNS;

  return (
    <div>
      {source && (
        <p style={{ margin: "0 0 0.5rem", fontSize: "0.8rem", padding: "0.5rem 0.75rem", borderRadius: "8px", background: "var(--color-surface-alt)", color: "var(--color-text-muted)" }}>
          {t(`admin.gameweekOps.sections.standings.provenance.${source}`)}
        </p>
      )}
      {hasAnyTie && (
        <p style={{ margin: "0 0 0.75rem", fontSize: "0.8rem", padding: "0.5rem 0.75rem", borderRadius: "8px", background: "color-mix(in srgb, var(--color-warning) 15%, transparent)", color: "var(--color-warning)" }}>
          {t("admin.gameweekOps.sections.standings.tieNotice")}
        </p>
      )}
      {table && (
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0 0.75rem 0.35rem" }}>
          <span style={{ width: "1.6rem", flexShrink: 0 }} />
          <span style={{ color: "transparent", flexShrink: 0, fontSize: "0.8rem" }}>⣿</span>
          <span style={{ width: 20, flexShrink: 0 }} />
          <span style={{ flex: 1 }} />
          {statColumns.map((col) => (
            <span key={col.key} style={{ ...statCellStyle, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.62rem" }}>{col.label}</span>
          ))}
          <div style={{ width: isMobile ? 0 : "3.4rem", flexShrink: 0 }} />
        </div>
      )}
      <div style={{ border: "1px solid var(--color-border)", borderRadius: "10px", overflow: "hidden" }}>
        {[...standingRows].sort((a, b) => a.position - b.position).map((row, i) => {
          const club = clubs.find((c) => c.id === row.clubId);
          const isDragTarget = standingDragVisual === i && standingDragIdx.current !== null && standingDragIdx.current !== i;
          const statRow = tableByClub.get(row.clubId);
          const isTied = statRow ? (tieCounts.get(tieKey(statRow)) ?? 0) > 1 : false;
          return (
            <div key={row.clubId} draggable
              onDragStart={(e) => { standingDragIdx.current = i; e.dataTransfer.effectAllowed = "move"; }}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; standingDragOverIdx.current = i; if (standingDragVisual !== i) setStandingDragVisual(i); }}
              onDragLeave={() => { if (standingDragOverIdx.current === i) { standingDragOverIdx.current = null; setStandingDragVisual(null); } }}
              onDrop={(e) => { e.preventDefault(); const from = standingDragIdx.current; if (from !== null && from !== i) moveStanding(from, i); standingDragIdx.current = null; standingDragOverIdx.current = null; setStandingDragVisual(null); }}
              onDragEnd={() => { standingDragIdx.current = null; standingDragOverIdx.current = null; setStandingDragVisual(null); }}
              style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.5rem 0.75rem", borderBottom: i < standingRows.length - 1 ? "1px solid var(--color-border-subtle)" : "none", background: isDragTarget ? "var(--color-surface-alt)" : "var(--color-surface)", borderLeft: isTied ? "3px solid var(--color-warning)" : "3px solid transparent", borderTop: isDragTarget ? "2px solid var(--color-accent)" : "2px solid transparent", cursor: "grab", userSelect: "none", transition: "background 0.1s" }}>
              <span style={{ width: "1.6rem", textAlign: "right", fontWeight: 700, fontSize: "1rem", color: "var(--color-text-muted)", flexShrink: 0 }}>{i + 1}</span>
              <span style={{ color: "var(--color-text-dim)", flexShrink: 0, fontSize: "0.8rem", pointerEvents: "none" }}>⣿</span>
              <span style={{ pointerEvents: "none", flexShrink: 0, lineHeight: 0 }}>
                <ClubBadge config={getClubBadge(club?.name ?? "")} size={20} title={club?.name ?? ""} />
              </span>
              <span style={{ flex: 1, fontSize: "0.88rem", fontWeight: 500, color: "var(--color-text)", pointerEvents: "none", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {club?.shortName && club.shortName !== club.name ? club.shortName : club?.name ?? row.clubId}
                {isTied && <span title={t("admin.gameweekOps.sections.standings.tieNotice")} style={{ marginLeft: "0.4rem", fontSize: "0.62rem", fontWeight: 700, color: "var(--color-warning)" }}>{t("admin.gameweekOps.sections.standings.tiedBadge")}</span>}
              </span>
              {statRow && statColumns.map((col) => (
                <span key={col.key} style={statCellStyle}>{col.key === "goalDifference" && statRow[col.key] > 0 ? `+${statRow[col.key]}` : statRow[col.key]}</span>
              ))}
              <div style={{ display: "flex", gap: "0.25rem", flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                <button disabled={i === 0} onClick={() => moveStanding(i, i - 1)} title="Move up"
                  style={{ background: "none", border: "1px solid var(--color-border)", borderRadius: "4px", cursor: i === 0 ? "not-allowed" : "pointer", fontSize: "0.7rem", padding: "0.1rem 0.35rem", color: i === 0 ? "var(--color-text-dim)" : "var(--color-text-muted)", opacity: i === 0 ? 0.4 : 1, lineHeight: 1.2 }}>▲</button>
                <button disabled={i === standingRows.length - 1} onClick={() => moveStanding(i, i + 1)} title="Move down"
                  style={{ background: "none", border: "1px solid var(--color-border)", borderRadius: "4px", cursor: i === standingRows.length - 1 ? "not-allowed" : "pointer", fontSize: "0.7rem", padding: "0.1rem 0.35rem", color: i === standingRows.length - 1 ? "var(--color-text-dim)" : "var(--color-text-muted)", opacity: i === standingRows.length - 1 ? 0.4 : 1, lineHeight: 1.2 }}>▼</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
